<script>
    import * as d3 from "d3";
    import { onMount } from "svelte";

    export let selected_keywords = [];
    export let thematic_keywords = [];
    export let w = 680;
    export let h = 680;
    export let on_keyword_selected = () => {};
    export let on_keyword_unselected = () => {};
    export let chart_el = null;

    let keyphrases_container_el = null;

    const zoomout = () => {
        const event = new CustomEvent("chart:zoom-out", { detail: {} });
        chart_el.dispatchEvent(event);
    };

    const create_keyphrases_cluster_chart = ({
        selected_keywords,
        thematic_keywords,
        w,
        h,
        on_keyword_selected = null,
        on_keyword_unselected = null,
    }) => {
        const data = {
            id: Math.random().toString(16).substring(2),
            name: "root",
            children: thematic_keywords.map((cluster) => ({
                id: Math.random().toString(16).substring(2),
                name: cluster.theme,
                children: cluster.pages.map((page) => ({
                    id: Math.random().toString(16).substring(2),
                    name: page.cluster_title,
                    children: page.keywords.map((keyword) => ({
                        id: Math.random().toString(16).substring(2),
                        name: keyword.keyword,
                        value: 1,
                        keyword: keyword,
                    })),
                })),
            })),
        };
        // Specify the chart's dimensions.
        const width = w || 1300;
        const height = h || 680;

        const colors = {
            expert: "#7986cb",
            page_has_expert: "#AED6F1",
            normal: "#FFF",
            selected_border: "#1a237e",
            text: "#333"
        }

        // Create the color scale.
        const circle_color = d3.scaleLinear().domain([0, 5]).range(["#FFF", "#396667"]).interpolate(d3.interpolateHcl);

        // Compute the layout.
        const pack = (data) =>
            d3.pack().size([width, height]).padding(3)(
                d3
                    .hierarchy(data)
                    .sum((d) => d.value)
                    .sort((a, b) => b.value - a.value)
            );
        const root = pack(data);

        let currentDepth = 0;

        // Create the SVG container.
        const svg = d3
            .create("svg")
            .attr("viewBox", `-${width / 2} -${height / 2} ${width} ${height}`)
            .attr("width", width)
            .attr("height", height)
            .attr("class", "packed-circle-chart");

        // Append the nodes.
        const node = svg
            .append("g")
            .attr("class", "g-circles")
            .selectAll("circle")
            .data(root.descendants().slice(1))
            .join("circle")
            .attr("fill", (d) => {
                if (d.data.keyword && d.data.keyword.expert) {
                    return colors.expert;
                }
                if (d.depth == 2) {
                    for (let child of d.data.children) {
                        if (child.keyword.expert) {
                            return colors.page_has_expert;
                        }
                    }
                }
                return d.children ? circle_color(d.depth) : "white";
            })
            .attr("data-selected", 0)
            .attr("data-id", (d) => {
                return d.data.id;
            })
            .attr("class", "circle")
            .on("mouseover", (event, d) => {
                if (focus === d) {
                    return;
                }
                if (isZooming) {
                    return
                }
                // Only highlight the current depth
                if (d.depth - 1 == currentDepth) {
                    const id = d.data.id;
                    // Increase font size for emphasis by scaling the group
                    const group = svg.select(`g.text-group[data-id="${id}"]`);
                    group
                        .transition()
                        .duration(500)
                        .ease(d3.easeCubicOut)
                        .attr("transform", function () {
                            const maxScale = 1.5;
                            const currentGroupTransform = d3.select(this).attr("transform") || "";
                            return currentGroupTransform.replace(/scale\([^)]+\)/, `scale(${maxScale})`).trim();
                        });

                    // Show rectangle background on the text
                    group.select("rect.text-bg").style("fill-opacity", 1).style("display", "inline");
                }
            })
            .on("mouseout", (event, d) => {
                const id = d.data.id;
                // Reset scale
                const group = svg.select(`g.text-group[data-id="${id}"]`);
                group
                    .transition()
                    .duration(500)
                    .ease(d3.easeCubicOut)
                    .attr("transform", function () {
                        const currentGroupTransform = d3.select(this).attr("transform") || "";
                        return currentGroupTransform.replace(/scale\([^)]+\)/, `scale(${d.scale})`).trim();
                    });

                // Hide the background rectangle
                group.select(`rect.text-bg`).style("fill-opacity", 0).style("display", "none");
            })
            .on("click", (event, d) => {
                if (d.depth == 3) {
                    const current_selected = event.currentTarget;
                    const is_selected = Number(current_selected.getAttribute("data-selected"));
                    const keyword = d.data.keyword;
                    if (is_selected) {
                        // Return to the appropriate color when deselected
                        if (d.data.keyword && d.data.keyword.expert) {
                            d3.select(current_selected).attr("fill", colors.expert).attr("data-selected", "0");
                        } else {
                            d3.select(current_selected).attr("fill", colors.normal).attr("data-selected", "0");
                        }
                        // Remove stroke
                        d3.select(current_selected)
                            .attr("stroke", "transparent")
                            .attr("stroke-width", 0)
                            .attr("data-selected", "0");
                        if (typeof on_keyword_unselected === "function") {
                            on_keyword_unselected(keyword);
                        }
                    } else {
                        d3.select(current_selected)
                            .attr("stroke", colors.selected_border)
                            .attr("stroke-width", "3px")
                            .attr("data-selected", "1");
                        if (typeof on_keyword_selected === "function") {
                            on_keyword_selected(keyword);
                        }
                    }

                    event.stopPropagation();
                    return;
                }

                if (focus !== d) {
                    zoom(event, d);
                }
                event.stopPropagation();
            });

        // Append the labels group
        const labels = svg
            .append("g")
            .attr("class", "g-labels")
            .selectAll("g")
            .data(root.descendants())
            .join("g")
            .attr("class", "text-group")
            .attr("data-id", (d) => d.data.id)
            .style("pointer-events", "none");

        // For each group append text element
        labels.each(function (d) {
            const group = d3.select(this);
            // Append the text element
            const text = group
                .append("text")
                .attr("class", "text-label")
                .style("font-family", "'Manrope', sans-serif")
                .style("font-weight", "bold")
                .style("fill", colors.text)
                .style("user-select", "none")
                .attr("text-anchor", "middle")
                .style("fill-opacity", (d) => (d.parent === root ? 1 : 0))
                .style("display", (d) => (d.parent === root ? "inline" : "none"))
                .style("pointer-events", "none")
                .text((d) => d.data.name)
                .each(function (d) {
                    const node = d3.select(this);
                    const words = d.data.name.split(/\s+/).reverse();
                    let line = [];
                    let lineNumber = 0;
                    let lineCharCount = 0;
                    let word;
                    // Clear any existing text content before appending new tspans
                    node.text("");
                    let tspan = node.append("tspan").attr("x", 0).attr("dy", "0em");

                    while ((word = words.pop())) {
                        lineCharCount += word.length;
                        line.push(word);
                        tspan.text(line.join(" "));

                        if (lineCharCount > 15 && line.length > 1) {
                            // console.log("breaking line for" + d.data.name);
                            line.pop();
                            tspan.text(line.join(" ")); // Restore the previous valid line
                            line = [word];
                            lineNumber++;
                            lineCharCount = 0;
                            tspan = node
                                .append("tspan")
                                .attr("x", 0)
                                .attr("dy", "1.2em") // Line spacing
                                .style("pointer-events", "none")
                                .text(word);
                        }
                    }
                });
        });

        // Append the legend
        const legend = svg
            .append("g")
            .attr("class", "g-legend")
            .attr("transform", `translate(${-width / 2 + 20}, ${height / 2 - 150})`);

        // Add a legend for the different node types
        const legendData = [
            { label: "Expert Keywords", color: colors.expert },
            { label: "Cluster with Experts", color: colors.page_has_expert },
            { label: "Regular Keywords", color: colors.normal },
        ];

        legend
            .selectAll("rect")
            .data(legendData)
            .enter()
            .append("rect")
            .attr("x", 0)
            .attr("y", (d, i) => i * 25)
            .attr("width", 20)
            .attr("height", 20)
            .attr("fill", (d) => d.color)
            .attr("stroke", "#333")
            .attr("stroke-width", 1);

        legend
            .selectAll("text")
            .data(legendData)
            .enter()
            .append("text")
            .attr("x", 30)
            .attr("y", (d, i) => i * 25 + 15)
            .text((d) => d.label)
            .attr("font-size", "12px")
            .attr("fill", colors.text);

        // Create the zoom behavior and zoom immediately in to the initial focus node. (root)
        svg.on("click", (event) => {
            zoom(event, root);
        });
        let focus = root;
        let view;
        let isZooming = false;
        zoomTo([focus.x, focus.y, focus.r * 2]);

        function zoomTo(v) {
            const k = width / v[2];

            view = v;

            // // Update the position of the label groups (g elements)
            labels.attr("transform", (d) => {
                const radius = (d.r || 1) * k;
                // Calculate dynamic scale based on radius
                // For larger radius values, use a max scale of 1
                // For smaller radius values, use a smaller scale to prevent label collisions
                const minScale = 0.2;
                const scale = Math.max(minScale, Math.min(1, radius / 40));
                d.scale = scale;
                return `translate(${(d.x - v[0]) * k},${(d.y - v[1]) * k}) scale(${scale})`;
            });
            // Update the position and radius of the nodes (circle elements)
            node.attr("transform", (d) => `translate(${(d.x - v[0]) * k},${(d.y - v[1]) * k})`);
            node.attr("r", (d) => d.r * k);
        }

        function zoom(event, d) {
            focus = d;
            currentDepth = d.depth;

            const transition = svg
                .transition()
                .duration(1000)
                .tween("zoom", (d) => {
                    const i = d3.interpolateZoom(view, [focus.x, focus.y, focus.r * 2]);
                    return (t) => zoomTo(i(t));
                });

            labels
                .selectChildren()
                // Filter the labels based on the focus node
                .filter(function (d) {
                    return d.parent === focus || this.style.display === "inline";
                })
                .transition(transition)
                // Set the fill opacity based on whether the parent is the focus node
                .style("fill-opacity", (d) => (d.parent === focus ? 1 : 0))
                // On transition start, set the display to "inline" if the parent is the focus node
                .on("start", function (d) {
                    isZooming = true;
                    if (d.parent === focus) {
                        if (this.tagName == "text") {
                            this.style.display = "inline";
                        }
                        // Keep the background rectangle hidden
                        if (this.tagName == "rect") {
                            this.style.display = "none";
                        }
                    }
                })
                // On transition end, set the display to "none" if the parent is not the focus node
                .on("end", function (d) {
                    isZooming = false;
                    if (d.parent !== focus) this.style.display = "none";
                    // Recalculate the rect with heght based on the text
                    if (this.tagName == "rect") {
                        const textElement = d3.select(this.parentNode).select("text");
                        const bbox = textElement.node().getBBox();
                        const paddingX = 5,
                            paddingY = 2;
                        const bgWidth = bbox.width + 2 * paddingX;
                        const bgHeight = bbox.height + 2 * paddingY;
                        const bgX = bbox.x - paddingX;
                        const bgY = bbox.y - paddingY;
                        d3.select(this)
                            .attr("x", bgX)
                            .attr("y", bgY)
                            .attr("width", bgWidth)
                            .attr("height", bgHeight)
                            .style("display", "inline")
                            .style("fill-opacity", 0)
                            .style("pointer-events", "none");
                    }
                });
        }

        const svgEl = svg.node();
        svgEl.addEventListener("chart:zoom-out", () => {
            // Zoom out one step back
            if (focus.parent) {
                zoom(null, focus.parent);
            }
        });

        svgEl.addEventListener("chart:ready", (e) => {
            // Requires the chart to be ready before prepending the background rect
            // getBBox() is not available until the chart is rendered
            labels.each(function (d) {
                // Get the bounding box of the text
                const textElement = d3.select(this).select("text");
                // Defer getBBox until after paint
                const group = d3.select(this);
                const bbox = textElement.node().getBBox();
                const paddingX = 5,
                    paddingY = 2;
                const bgWidth = bbox.width + 2 * paddingX;
                const bgHeight = bbox.height + 2 * paddingY;
                const bgX = bbox.x - paddingX;
                const bgY = bbox.y - paddingY;
                group
                    .insert("rect", "text")
                    .attr("class", "text-bg")
                    .attr("x", bgX)
                    .attr("y", bgY)
                    .attr("width", bgWidth)
                    .attr("height", bgHeight)
                    .attr("rx", 5)
                    .attr("ry", 5)
                    .style("fill-opacity", 0)
                    .attr("fill", "rgba(255,255,255,0.8)")
                    .style("pointer-events", "none");
            });

            // for (let keyword of selected_keywords) {
            //     svgEl.dispatchEvent(
            //         new CustomEvent("chart:select", {
            //             detail: {
            //                 keyword: keyword.keyword,
            //             },
            //         })
            //     );
            // }
        });

        svgEl.addEventListener("chart:unselect", (e) => {
            const id = e.detail.id;
            const foundNode = svg.selectAll("circle").filter(function (d) {
                return d.depth === 3 && d.data.keyword && d.data.keyword.id === id;
            });
            if (foundNode.size() > 0) {
                foundNode.attr("stoke", "transparent").attr("stroke-width", 0).attr("data-selected", "0");
                if (typeof on_keyword_unselected === "function") {
                    on_keyword_unselected(foundNode.datum().data.keyword);
                }
            }
        });

        svgEl.addEventListener("chart:select", (e) => {
            const keyword = e.detail.keyword;
            // Find the cluster and node containing this keyword
            const foundNode = svg.selectAll("circle").filter(function (d) {
                return d.depth === 3 && d.data.keyword && d.data.keyword.keyword === keyword;
            });

            if (foundNode.size() > 0) {
                // Get the node data and ID
                const nodeData = foundNode.datum();

                // Select the circle visually
                foundNode.attr("fill", "#b9f399").attr("data-selected", "1");

                // If the node is not visible, zoom to its parent to make it visible
                if (nodeData.parent !== focus) {
                    zoom(null, nodeData.parent);
                }

                // Trigger keyword selection handler
                if (typeof on_keyword_selected === "function") {
                    on_keyword_selected(nodeData.data.keyword);
                }
            }
        });

        svgEl.addEventListener("chart:reset", (e) => {
            // Reset the chart to the initial state
            focus = root;
            currentDepth = 0;
            zoomTo([focus.x, focus.y, focus.r * 2]);
            // Reset all circles to their original color
            node.attr("fill", (d) => {
                if (d.data.keyword && d.data.keyword.expert) {
                    return colors.expert;
                }
                if (d.depth == 2) {
                    for (let child of d.data.children) {
                        if (child.keyword.expert) {
                            return colors.page_has_expert;
                        }
                    }
                }
                return d.children ? circle_color(d.depth) : "white";
            });
            node.attr("data-selected", 0);
            node.attr("stroke", "transparent").attr("stroke-width", 0);
            labels.selectAll("text").style("fill-opacity", (d) => (d.parent === root ? 1 : 0));
            labels.selectAll("rect.text-bg").style("fill-opacity", 0).style("display", "none");
            labels.attr("transform", (d) => {
                const radius = (d.r || 1) * 1;
                d.scale = 1;
                return `translate(${(d.x - view[0]) * 1},${(d.y - view[1]) * 1}) scale(${1})`;
            });
            labels.selectAll("text").style("display", (d) => (d.parent === root ? "inline" : "none"));
            labels.selectAll("rect.text-bg").style("display", "none");
            labels.selectAll("rect.text-bg").style("pointer-events", "none");
            labels.selectAll("text").style("pointer-events", "none");
            labels.selectAll("rect.text-bg").style("pointer-events", "none");
            labels.selectAll("g.text-group").style("pointer-events", "none");
        });

        return svgEl;
    };

    onMount(() => {
        chart_el = create_keyphrases_cluster_chart({
            selected_keywords,
            thematic_keywords,
            w,
            h,
            on_keyword_selected,
            on_keyword_unselected,
        });

        // Append the chart to the container element
        keyphrases_container_el.prepend(chart_el);

        // Make sure chart appended to the DOM, then trigger chart:ready event
        setTimeout(() => {
            const event = new CustomEvent("chart:ready", { detail: {} });
            chart_el.dispatchEvent(event);
        }, 250);

    });
</script>

<div class="animate-in keyphrases-svg-container" bind:this={keyphrases_container_el}>
    <button class="btn btn-primary btn-keyphrases-zoomout" on:click={zoomout}>Back</button>
</div>

<style>
    .keyphrases-svg-container {
        height: 100%;
    }
</style>